import { useEffect, useState } from 'react';
import Button from '../../../../components/Button';
import { getJourneyItemTemplateLink, getJourneyUserItemLink } from '../../../../utils/linkUtils';
import {
    JourneyChapterStatus,
    JourneyItemStatus,
    JourneySectionStatus,
    type JourneyChapterProgressFirestore,
    type JourneyItemProgressFirestore,
    type JourneySectionProgressFirestore,
    type UserJourneyTree,
} from '../../services/journeyTypes';

interface UserProgressTreeProps {
    tree: UserJourneyTree;
    userId: string;
    onChapterStatusChange: (chapterId: string, status: JourneyChapterStatus) => Promise<void>;
    onSectionStatusChange: (chapterId: string, sectionId: string, status: JourneySectionStatus) => Promise<void>;
    onItemChange: (
        chapterId: string,
        sectionId: string,
        itemId: string,
        patch: { status?: JourneyItemStatus; progress?: number },
    ) => Promise<void>;
    onChatReset: (
        chapterId: string,
        sectionId: string,
        itemId: string,
        options: { clearOcdProfile: boolean },
    ) => Promise<void>;
}

const STATUS_BADGE_CLASS: Record<string, string> = {
    locked: 'bg-gray-200 text-gray-600',
    available: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-700',
    claimed: 'bg-purple-100 text-purple-700',
};

const StatusBadge = ({ status }: { status: string }) => (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_CLASS[status] ?? 'bg-gray-100 text-gray-600'}`}>
        {status}
    </span>
);

const selectClasses = 'px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';

const StatusSelect = <T extends string>({
    value,
    options,
    disabled,
    onChange,
}: {
    value: T;
    options: T[];
    disabled: boolean;
    onChange: (value: T) => void;
}) => (
    <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as T)}
        className={selectClasses}
    >
        {options.map((option) => (
            <option key={option} value={option}>{option}</option>
        ))}
    </select>
);

const ITEM_STATUS_OPTIONS = Object.values(JourneyItemStatus);
const SECTION_STATUS_OPTIONS = Object.values(JourneySectionStatus);
const CHAPTER_STATUS_OPTIONS = Object.values(JourneyChapterStatus);

const linkClasses = 'text-xs font-medium text-blue-600 underline hover:text-blue-800';

const ItemRow = ({
    chapterId,
    sectionId,
    item,
    userId,
    onItemChange,
    onChatReset,
}: {
    chapterId: string;
    sectionId: string;
    item: JourneyItemProgressFirestore;
    userId: string;
    onItemChange: UserProgressTreeProps['onItemChange'];
    onChatReset: UserProgressTreeProps['onChatReset'];
}) => {
    const [progressInput, setProgressInput] = useState(String(item.progress ?? 0));
    const [isSaving, setIsSaving] = useState(false);
    const [clearOcdProfile, setClearOcdProfile] = useState(false);

    useEffect(() => {
        setProgressInput(String(item.progress ?? 0));
    }, [item.progress]);

    const handleStatusChange = async (status: JourneyItemStatus) => {
        setIsSaving(true);
        try {
            await onItemChange(chapterId, sectionId, item.id, { status });
        } finally {
            setIsSaving(false);
        }
    };

    const handleProgressSave = async () => {
        const parsed = Number(progressInput);
        if (Number.isNaN(parsed) || parsed === item.progress) return;
        setIsSaving(true);
        try {
            await onItemChange(chapterId, sectionId, item.id, { progress: parsed });
        } finally {
            setIsSaving(false);
        }
    };

    const handleChatReset = async () => {
        const profileWarning = clearOcdProfile
            ? `\n\nIt will ALSO wipe the inferred OCD profile values for: ${Object.keys(item.fieldsToCollect ?? {}).join(', ') || '(no fields)'}.`
            : '';
        const confirmed = window.confirm(
            `Reset the chat quest "${item.title}" for this user?\n\nProgress goes back to 0 (status: available) and their on-device conversation is wiped the next time they open the app.${profileWarning}`,
        );
        if (!confirmed) return;

        setIsSaving(true);
        try {
            await onChatReset(chapterId, sectionId, item.id, { clearOcdProfile });
            setClearOcdProfile(false);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
            <span className="w-6 text-xs text-gray-400">{item.order}</span>
            <div className="min-w-40 flex-1">
                <div className="text-sm font-medium text-gray-800 break-words">{item.title}</div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    <span>{item.type}</span>
                    <span className="font-mono text-gray-400 break-all">{item.templateId}</span>
                    <a
                        href={getJourneyItemTemplateLink(chapterId, sectionId, item.templateId)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClasses}
                    >
                        Template
                    </a>
                    <a
                        href={getJourneyUserItemLink(userId, chapterId, sectionId, item.id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={linkClasses}
                    >
                        User Item
                    </a>
                </div>

                {item.type === 'chat' && (
                    <div className="flex flex-wrap items-center gap-2 border-l border-gray-200 mt-2">
                        <label className="flex items-center gap-1 text-xs text-gray-500">
                            <input
                                type="checkbox"
                                checked={clearOcdProfile}
                                onChange={(e) => setClearOcdProfile(e.target.checked)}
                                disabled={isSaving}
                            />
                            clear OCD profile
                        </label>
                        <Button onClick={handleChatReset} variant="outline" size="sm" disabled={isSaving}>
                            Reset chat
                        </Button>
                        {item.chatResetAt != null && (
                            <span className="text-xs text-gray-400" title="Last reset">
                                {new Date(item.chatResetAt).toLocaleString()}
                            </span>
                        )}
                    </div>
                )}
            </div>

            <StatusBadge status={item.status} />
            <StatusSelect
                value={item.status}
                options={ITEM_STATUS_OPTIONS}
                disabled={isSaving}
                onChange={handleStatusChange}
            />

            <div className="flex items-center gap-1">
                <label className="text-xs text-gray-500">progress</label>
                <input
                    type="number"
                    value={progressInput}
                    onChange={(e) => setProgressInput(e.target.value)}
                    disabled={isSaving}
                    className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {item.maxProgress != null && (
                    <span className="text-xs text-gray-400">/ {item.maxProgress}</span>
                )}
                <Button
                    onClick={handleProgressSave}
                    variant="secondary"
                    size="sm"
                    disabled={isSaving || Number(progressInput) === item.progress}
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

const SectionBlock = ({
    chapterId,
    section,
    userId,
    onSectionStatusChange,
    onItemChange,
    onChatReset,
}: {
    chapterId: string;
    section: JourneySectionProgressFirestore;
    userId: string;
    onSectionStatusChange: UserProgressTreeProps['onSectionStatusChange'];
    onItemChange: UserProgressTreeProps['onItemChange'];
    onChatReset: UserProgressTreeProps['onChatReset'];
}) => {
    const [isSaving, setIsSaving] = useState(false);

    const handleStatusChange = async (status: JourneySectionStatus) => {
        setIsSaving(true);
        try {
            await onSectionStatusChange(chapterId, section.id, status);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="ml-2 sm:ml-4 rounded-md border border-gray-200 bg-white p-3">
            <div className="mb-2 flex flex-wrap items-center gap-3">
                <span className="w-6 text-xs text-gray-400">{section.order}</span>
                <span className="flex-1 font-semibold text-gray-800">{section.header}</span>
                <StatusBadge status={section.status} />
                <StatusSelect
                    value={section.status}
                    options={SECTION_STATUS_OPTIONS}
                    disabled={isSaving}
                    onChange={handleStatusChange}
                />
            </div>

            <div className="flex flex-col gap-2">
                {section.items.map((item) => (
                    <ItemRow
                        key={item.id}
                        chapterId={chapterId}
                        sectionId={section.id}
                        item={item}
                        userId={userId}
                        onItemChange={onItemChange}
                        onChatReset={onChatReset}
                    />
                ))}
                {section.items.length === 0 && (
                    <p className="text-xs text-gray-400">No items compiled for this section.</p>
                )}
            </div>
        </div>
    );
};

const ChapterBlock = ({
    chapter,
    userId,
    onChapterStatusChange,
    onSectionStatusChange,
    onItemChange,
    onChatReset,
}: {
    chapter: JourneyChapterProgressFirestore;
    userId: string;
    onChapterStatusChange: UserProgressTreeProps['onChapterStatusChange'];
    onSectionStatusChange: UserProgressTreeProps['onSectionStatusChange'];
    onItemChange: UserProgressTreeProps['onItemChange'];
    onChatReset: UserProgressTreeProps['onChatReset'];
}) => {
    const [isSaving, setIsSaving] = useState(false);

    const handleStatusChange = async (status: JourneyChapterStatus) => {
        setIsSaving(true);
        try {
            await onChapterStatusChange(chapter.id, status);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="rounded-lg border border-gray-300 bg-gray-50 p-4">
            <div className="mb-3 flex flex-wrap items-center gap-3">
                <span className="text-lg font-bold text-gray-900">
                    Ch {chapter.number}: {chapter.title}
                </span>
                <StatusBadge status={chapter.status} />
                <StatusSelect
                    value={chapter.status}
                    options={CHAPTER_STATUS_OPTIONS}
                    disabled={isSaving}
                    onChange={handleStatusChange}
                />
            </div>

            <div className="flex flex-col gap-3">
                {chapter.sections.map((section) => (
                    <SectionBlock
                        key={section.id}
                        chapterId={chapter.id}
                        section={section}
                        userId={userId}
                        onSectionStatusChange={onSectionStatusChange}
                        onItemChange={onItemChange}
                        onChatReset={onChatReset}
                    />
                ))}
                {chapter.sections.length === 0 && (
                    <p className="text-xs text-gray-400">No sections compiled for this chapter.</p>
                )}
            </div>
        </div>
    );
};

export const UserProgressTree = ({
    tree,
    userId,
    onChapterStatusChange,
    onSectionStatusChange,
    onItemChange,
    onChatReset,
}: UserProgressTreeProps) => {
    return (
        <div className="flex flex-col gap-4">
            {tree.map((chapter) => (
                <ChapterBlock
                    key={chapter.id}
                    chapter={chapter}
                    userId={userId}
                    onChapterStatusChange={onChapterStatusChange}
                    onSectionStatusChange={onSectionStatusChange}
                    onItemChange={onItemChange}
                    onChatReset={onChatReset}
                />
            ))}
        </div>
    );
};
